import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  APPROVAL_DECISION,
  REVIEW_DECISION,
  type ApprovalDecision,
  type ReviewDecision,
} from '@lims/shared-types';
import {
  approvalApi,
  resultHistoryApi,
  reviewApi,
  type Attempt,
  type ResultHistoryView,
} from '../../api/endpoints.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  Dialog,
  TextAreaField,
  useToast,
} from '../../components/ui/index.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';

/**
 * THE ACTS OF REVIEW, APPROVAL AND CORRECTION.
 *
 * ===========================================================================
 * READ CLAUDE.md SECTIONS 5 AND 8 BEFORE CHANGING ANYTHING HERE.
 * ===========================================================================
 *
 * WHAT THESE CONTROLS ARE NOT. They are not the authorization. Every button
 * below is shown according to the hints the backend already computed
 * (`canReview`, `canApprove`, `canCorrect` — permission AND state AND "not your
 * own work"), and every endpoint behind them re-checks all three, plus a
 * database trigger that refuses self-review and self-approval independently.
 * A person who edits the URL, or skips this application entirely, is refused
 * there. Hiding a button protects nothing.
 *
 * WHAT THEY SAY. Accepting says the RECORD passed a technical check. Approving
 * says a second person checked the same RECORD. NEITHER STATES THAT THE
 * MATERIAL PASSED, and the wording on every screen says so in both languages
 * (decision D104).
 *
 * EVERY ACT NAMES ITS REVISION. The number travels with the request, so a
 * result that was returned and resubmitted while somebody was reading it is
 * refused with a conflict rather than accepted unread.
 *
 * NOTHING ADVANCES BECAUSE A BUTTON WAS PRESSED. Each dialog closes on the
 * server's answer, and the toast reports what the server actually did.
 */

/** Reason text the backend will accept: substance, not a single character. */
const MINIMUM_REASON = 3;

// ---------------------------------------------------------------------------
// Technical review — three decisions, two of which must say why
// ---------------------------------------------------------------------------

export function ReviewActions({
  history,
  onActed,
}: {
  readonly history: ResultHistoryView;
  readonly onActed: (next: Attempt, message: string) => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const [choice, setChoice] = useState<ReviewDecision | null>(null);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');

  const revisionNumber = history.latestRevisionNumber;

  const act = useMutation({
    mutationFn: () => {
      const attemptId = history.attemptId;
      // Guarded by the disabled state below; the server refuses it in any case.
      const revision = revisionNumber ?? 0;
      switch (choice) {
        case REVIEW_DECISION.TECHNICALLY_ACCEPTED:
          return reviewApi.accept(attemptId, {
            revisionNumber: revision,
            ...(note.trim() ? { note: note.trim() } : {}),
          });
        case REVIEW_DECISION.RETURNED_FOR_CORRECTION:
          return reviewApi.return(attemptId, {
            revisionNumber: revision,
            reason: reason.trim(),
          });
        case REVIEW_DECISION.RETEST_REQUIRED:
          return reviewApi.requireRetest(attemptId, {
            revisionNumber: revision,
            reason: reason.trim(),
          });
        default:
          return Promise.reject(new Error('no decision'));
      }
    },
    onSuccess: async (next) => {
      const decided = choice;
      setChoice(null);
      setReason('');
      setNote('');
      await onActed(
        next,
        t('review.recorded', { decision: t(`reviewDecision.${decided}`) }),
      );
      show({
        tone: 'success',
        message: t('review.recorded', { decision: t(`reviewDecision.${decided}`) }),
      });
    },
  });

  const open = (next: ReviewDecision) => {
    setReason('');
    setNote('');
    act.reset();
    setChoice(next);
  };

  const reasonRequired = choice !== null && choice !== REVIEW_DECISION.TECHNICALLY_ACCEPTED;
  const mayConfirm =
    revisionNumber !== null && (!reasonRequired || reason.trim().length >= MINIMUM_REASON);

  return (
    <>
      <ActionBar>
        <ActionButton
          priority="tertiary"
          onPress={() => open(REVIEW_DECISION.RETEST_REQUIRED)}
        >
          {t('review.requireRetest')}
        </ActionButton>
        <ActionButton
          priority="secondary"
          onPress={() => open(REVIEW_DECISION.RETURNED_FOR_CORRECTION)}
        >
          {t('review.returnForCorrection')}
        </ActionButton>
        <ActionButton
          priority="primary"
          onPress={() => open(REVIEW_DECISION.TECHNICALLY_ACCEPTED)}
        >
          {t('review.acceptTechnically')}
        </ActionButton>
      </ActionBar>

      <Dialog
        isOpen={choice !== null}
        onClose={() => setChoice(null)}
        title={choice ? t(`reviewDecision.${choice}`) : ''}
        footer={
          <ActionBar>
            <ActionButton
              priority="tertiary"
              onPress={() => setChoice(null)}
              isDisabled={act.isPending}
            >
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isDisabled={!mayConfirm}
              isLoading={act.isPending}
              onPress={() => act.mutate()}
            >
              {t('common.confirm')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="flex flex-col gap-stack">
          {revisionNumber === null ? (
            <Alert tone="warning" title={t('review.noRevisionTitle')} description={t('review.noRevisionBody')} />
          ) : (
            <p className="text-caption text-default-600">
              {t('review.actingOnRevision', { number: revisionNumber })}
            </p>
          )}

          {choice === REVIEW_DECISION.TECHNICALLY_ACCEPTED ? (
            <Alert
              tone="info"
              title={t('review.acceptNoticeTitle')}
              description={t('review.acceptNoticeBody')}
            />
          ) : null}
          {choice === REVIEW_DECISION.RETURNED_FOR_CORRECTION ? (
            <Alert
              tone="warning"
              title={t('review.returnNoticeTitle')}
              description={t('review.returnNoticeBody')}
            />
          ) : null}
          {choice === REVIEW_DECISION.RETEST_REQUIRED ? (
            <Alert
              tone="warning"
              title={t('review.retestNoticeTitle')}
              description={t('review.retestNoticeBody')}
            />
          ) : null}

          {reasonRequired ? (
            <TextAreaField
              label={t('common.reason')}
              description={t('review.reasonShownVerbatim')}
              value={reason}
              onChange={setReason}
              isRequired
              rows={3}
            />
          ) : (
            <TextAreaField
              label={t('review.noteLabel')}
              description={t('common.optional')}
              value={note}
              onChange={setNote}
              rows={3}
            />
          )}

          {act.error ? <ErrorNotice error={act.error} /> : null}
        </div>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Final approval — a separate act, by a separate permission
// ---------------------------------------------------------------------------

export function ApprovalActions({
  history,
  onActed,
}: {
  readonly history: ResultHistoryView;
  readonly onActed: (next: Attempt, message: string) => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const [choice, setChoice] = useState<ApprovalDecision | null>(null);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');

  const revisionNumber = history.latestRevisionNumber;

  const act = useMutation({
    mutationFn: () => {
      const attemptId = history.attemptId;
      const revision = revisionNumber ?? 0;
      if (choice === APPROVAL_DECISION.APPROVED) {
        return approvalApi.approve(attemptId, {
          revisionNumber: revision,
          ...(note.trim() ? { note: note.trim() } : {}),
        });
      }
      return approvalApi.return(attemptId, {
        revisionNumber: revision,
        reason: reason.trim(),
      });
    },
    onSuccess: async (next) => {
      const decided = choice;
      setChoice(null);
      setReason('');
      setNote('');
      const message =
        decided === APPROVAL_DECISION.APPROVED
          ? t('approval.recordedApproved')
          : t('approval.recordedReturned');
      await onActed(next, message);
      show({ tone: 'success', message });
    },
  });

  const open = (next: ApprovalDecision) => {
    setReason('');
    setNote('');
    act.reset();
    setChoice(next);
  };

  const reasonRequired = choice === APPROVAL_DECISION.RETURNED_FOR_CORRECTION;
  const mayConfirm =
    revisionNumber !== null && (!reasonRequired || reason.trim().length >= MINIMUM_REASON);

  return (
    <>
      <ActionBar>
        <ActionButton
          priority="secondary"
          onPress={() => open(APPROVAL_DECISION.RETURNED_FOR_CORRECTION)}
        >
          {t('approval.returnForCorrection')}
        </ActionButton>
        <ActionButton priority="primary" onPress={() => open(APPROVAL_DECISION.APPROVED)}>
          {t('approval.approve')}
        </ActionButton>
      </ActionBar>

      <Dialog
        isOpen={choice !== null}
        onClose={() => setChoice(null)}
        title={choice ? t(`approvalDecision.${choice}`) : ''}
        footer={
          <ActionBar>
            <ActionButton
              priority="tertiary"
              onPress={() => setChoice(null)}
              isDisabled={act.isPending}
            >
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isDisabled={!mayConfirm}
              isLoading={act.isPending}
              onPress={() => act.mutate()}
            >
              {t('common.confirm')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="flex flex-col gap-stack">
          {revisionNumber === null ? (
            <Alert tone="warning" title={t('review.noRevisionTitle')} description={t('review.noRevisionBody')} />
          ) : (
            <p className="text-caption text-default-600">
              {t('review.actingOnRevision', { number: revisionNumber })}
            </p>
          )}

          {/*
            DECISION D104, restated at the moment of the act and not only at the
            top of the page. Approving records that the RECORD was checked. It
            does not state that the material passed, and there is deliberately
            no field here in which to claim that.
          */}
          {choice === APPROVAL_DECISION.APPROVED ? (
            <>
              <Alert
                tone="info"
                title={t('approval.recordCheckedTitle')}
                description={t('approval.recordCheckedBody')}
              />
              <Alert
                tone="warning"
                title={t('approval.locksTitle')}
                description={t('approval.locksBody')}
              />
            </>
          ) : (
            <Alert
              tone="warning"
              title={t('approval.returnNoticeTitle')}
              description={t('approval.returnNoticeBody')}
            />
          )}

          {reasonRequired ? (
            <TextAreaField
              label={t('common.reason')}
              description={t('review.reasonShownVerbatim')}
              value={reason}
              onChange={setReason}
              isRequired
              rows={3}
            />
          ) : (
            <TextAreaField
              label={t('review.noteLabel')}
              description={t('common.optional')}
              value={note}
              onChange={setNote}
              rows={3}
            />
          )}

          {act.error ? <ErrorNotice error={act.error} /> : null}
        </div>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// A post-approval correction — a recorded procedure, never an edit
// ---------------------------------------------------------------------------

export function CorrectionAction({
  history,
  onActed,
}: {
  readonly history: ResultHistoryView;
  readonly onActed: (next: Attempt, message: string) => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const [isOpen, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [summary, setSummary] = useState('');

  const open = useMutation({
    mutationFn: () =>
      resultHistoryApi.openCorrection(history.attemptId, {
        reason: reason.trim(),
        ...(summary.trim() ? { summary: summary.trim() } : {}),
      }),
    onSuccess: async (next) => {
      setOpen(false);
      setReason('');
      setSummary('');
      await onActed(next, t('corrections.opened'));
      show({ tone: 'success', message: t('corrections.opened') });
    },
  });

  return (
    <>
      <ActionButton
        priority="secondary"
        onPress={() => {
          setReason('');
          setSummary('');
          open.reset();
          setOpen(true);
        }}
      >
        {t('corrections.open')}
      </ActionButton>

      <Dialog
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        title={t('corrections.open')}
        footer={
          <ActionBar>
            <ActionButton
              priority="tertiary"
              onPress={() => setOpen(false)}
              isDisabled={open.isPending}
            >
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isDisabled={reason.trim().length < MINIMUM_REASON}
              isLoading={open.isPending}
              onPress={() => open.mutate()}
            >
              {t('common.confirm')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="flex flex-col gap-stack">
          <Alert
            tone="warning"
            title={t('corrections.openNoticeTitle')}
            description={t('corrections.openNoticeBody')}
          />
          <TextAreaField
            label={t('common.reason')}
            description={t('review.reasonShownVerbatim')}
            value={reason}
            onChange={setReason}
            isRequired
            rows={3}
          />
          <TextAreaField
            label={t('corrections.summaryLabel')}
            description={t('common.optional')}
            value={summary}
            onChange={setSummary}
            rows={2}
          />
          {open.error ? <ErrorNotice error={open.error} /> : null}
        </div>
      </Dialog>
    </>
  );
}
